import {
  type AgentActionPayload,
  type ApprovalDecision,
  type ApprovalRequestedPayload,
  type ArtifactCreatedPayload,
  type RunEvent,
  type RunResponse,
  type WorkspaceReadiness,
  WorkspaceReadinessCheckKeys,
  isApprovalRequestedPayload,
} from "@carbon-operations/contracts";
import { approvalActionContextDigestMatches, approvalInputsAndExecutionMatchRun } from "./approval-context-integrity.js";

/**
 * The membership as it applies to one workspace. The session's `roles` is a union over workspaces; when the server
 * sends `workspace_roles`, only that workspace's roles count (none if it is missing). Older sessions keep the union.
 */
export function workspaceLocalMembership<M extends { roles: string[]; workspace_roles?: Record<string, string[]> }>(
  membership: M,
  workspaceId: string,
): M {
  if (membership.workspace_roles === undefined) return membership;
  const local = membership.workspace_roles[workspaceId] ?? [];
  return { ...membership, roles: local.filter((role) => membership.roles.includes(role)) };
}

export function buildApprovalDecision(
  decision: "approve" | "deny",
  reason: string,
  expectedActionSha256: string,
  expiresAt: string,
): ApprovalDecision {
  return {
    schema: "carbon.operations.approval.decision/v1",
    decision,
    reason,
    expected_action_sha256: expectedActionSha256,
    expires_at: expiresAt,
  };
}

export function approvalRequestFor(
  events: RunEvent[],
  approvalId: string,
): ApprovalRequestedPayload | null {
  const event = [...events].reverse().find((candidate) =>
    candidate.type === "approval.requested" &&
    candidate.payload["approval_id"] === approvalId
  );
  if (!event) return null;
  return isApprovalRequestedPayload(event.payload) ? event.payload : null;
}

/** The event context must describe the same immutable request facts as the acknowledged run response. */
export function approvalRequestMatchesRun(
  approval: ApprovalRequestedPayload,
  run: RunResponse,
): boolean {
  const context = approval.action_context;
  try {
    return context.tenant_id === run.tenant_id && context.workspace_id === run.workspace_id &&
      approvalInputsAndExecutionMatchRun(context, run);
  } catch {
    return false;
  }
}

/** The decision-bearing event is usable only when its run facts and recomputed digest both match. */
export async function verifiedApprovalRequestForRun(
  events: RunEvent[],
  approvalId: string,
  run: RunResponse,
): Promise<ApprovalRequestedPayload | null> {
  const approval = approvalRequestFor(events, approvalId);
  return approval !== null && approvalRequestMatchesRun(approval, run) &&
      await approvalActionContextDigestMatches(approval)
    ? approval
    : null;
}

const agentActionClasses = new Set([
  "deploy", "release", "data-change", "outbound-message", "spend", "credential-change", "infra-change", "code-merge", "other",
]);

/** The ops.agent.action.v1 fields from approval.requested: undefined when absent, null when malformed. */
export function agentActionFacts(value: unknown): AgentActionPayload | null | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const fields = value as Record<string, unknown>;
  const { project, action_class: actionClass, target, summary, plan_sha256: planSha256, risk } = fields;
  const length = (text: string) => Array.from(text).length;
  if (
    Object.keys(fields).sort().join(",") !== "action_class,plan_sha256,project,risk,summary,target" ||
    typeof project !== "string" || !/^[a-z0-9][a-z0-9-]{1,62}$/.test(project) ||
    typeof actionClass !== "string" || !agentActionClasses.has(actionClass) ||
    typeof target !== "string" || length(target) < 1 || length(target) > 256 ||
    typeof summary !== "string" || length(summary) < 1 || length(summary) > 2_000 ||
    typeof planSha256 !== "string" || !/^[a-f0-9]{64}$/.test(planSha256) ||
    (risk !== "routine" && risk !== "high")
  ) return null;
  return {
    project,
    action_class: actionClass as AgentActionPayload["action_class"],
    target,
    summary,
    plan_sha256: planSha256,
    risk,
  };
}

export function artifactEvidenceFrom(
  events: RunEvent[],
): ArtifactCreatedPayload[] {
  return events.flatMap((event) => {
    if (event.type !== "artifact.created") return [];
    const payload = event.payload;
    const artifactId = payload["artifact_id"];
    const artifactRef = payload["artifact_ref"];
    const byteLength = payload["byte_length"];
    const sha256 = payload["sha256"];
    const runnerId = payload["runner_id"];
    const createdAt = payload["created_at"];
    const readbackVerifiedAt = payload["readback_verified_at"];
    if (
      typeof artifactId !== "string" ||
      !/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(artifactId) ||
      artifactRef !== `artifact://${artifactId}` ||
      payload["kind"] !== "document" ||
      payload["media_type"] !== "text/markdown" ||
      !Number.isInteger(byteLength) ||
      typeof byteLength !== "number" ||
      byteLength < 0 ||
      byteLength > 10 * 1_024 * 1_024 ||
      typeof sha256 !== "string" ||
      !/^[a-f0-9]{64}$/.test(sha256) ||
      payload["immutable"] !== true ||
      payload["template_version"] !== "operations-summary-v1" ||
      typeof runnerId !== "string" ||
      runnerId.length === 0 ||
      typeof createdAt !== "string" ||
      !Number.isFinite(Date.parse(createdAt)) ||
      typeof readbackVerifiedAt !== "string" ||
      !Number.isFinite(Date.parse(readbackVerifiedAt))
    ) return [];
    return [{
      artifact_id: artifactId,
      artifact_ref: artifactRef,
      kind: "document",
      media_type: "text/markdown",
      byte_length: byteLength,
      sha256,
      immutable: true,
      template_version: "operations-summary-v1",
      runner_id: runnerId,
      created_at: createdAt,
      readback_verified_at: readbackVerifiedAt,
    }];
  });
}

export function artifactIdFromRef(ref: string): string | null {
  const match = /^artifact:\/\/([A-Za-z0-9][A-Za-z0-9_-]*)$/.exec(ref);
  return match?.[1] ?? null;
}

export function eventsMatchRunBoundary(
  events: RunEvent[],
  boundary: { tenantId: string; workspaceId: string; runId: string },
): boolean {
  let previousSequence = 0;
  for (const event of events) {
    if (
      event.tenant_id !== boundary.tenantId ||
      event.workspace_id !== boundary.workspaceId ||
      event.run_id !== boundary.runId ||
      !Number.isInteger(event.sequence) ||
      event.sequence <= previousSequence
    ) return false;
    previousSequence = event.sequence;
  }
  return true;
}

export function readinessMatchesBoundary(
  readiness: WorkspaceReadiness,
  boundary: { tenantId: string; workspaceId: string },
): boolean {
  const allSatisfied = readiness.checks.every((item) =>
    item.status === "satisfied"
  );
  return readiness.schema === "carbon.operations.workspace-readiness/v1" &&
    readiness.tenant_id === boundary.tenantId &&
    readiness.workspace_id === boundary.workspaceId &&
    Number.isInteger(readiness.revision) && readiness.revision >= 0 &&
    readiness.checks.length === WorkspaceReadinessCheckKeys.length &&
    readiness.checks.every((item, index) =>
      item.key === WorkspaceReadinessCheckKeys[index]
    ) &&
    readiness.state === (allSatisfied ? "ready" : "incomplete") &&
    readiness.execution_enabled === allSatisfied;
}
